import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export type ExportFormat = 'csv' | 'pdf' | 'xlsx'

export interface ExportColumn {
  header: string
  key: string
  width?: number
}

export interface ExportSection {
  title: string
  columns: ExportColumn[]
  data: Record<string, any>[]
}

export interface SummaryItem {
  label: string
  value: string | number
}

export interface ExportConfig {
  title: string
  subtitle?: string
  filename: string
  institution?: {
    name: string
    logo?: string
    address?: string
    phone?: string
    email?: string
  }
  dateRange?: {
    from: string
    to: string
  }
  generatedAt?: string
  summary?: SummaryItem[]
  sections: ExportSection[]
}

// SmartTag School brand colors
const BRAND_COLORS = {
  primary: '#2563EB',
  primaryRgb: [37, 99, 235] as [number, number, number],
  secondary: '#1E40AF',
  secondaryRgb: [30, 64, 175] as [number, number, number],
  success: '#10B981',
  successRgb: [16, 185, 129] as [number, number, number],
  text: '#1F2937',
  textRgb: [31, 41, 55] as [number, number, number],
  lightGray: '#F3F4F6',
  lightGrayRgb: [243, 244, 246] as [number, number, number],
}

/**
 * Export data to CSV format
 */
export const exportToCSV = (config: ExportConfig): void => {
  const lines: string[] = []

  // Header info
  lines.push(`"${config.title}"`)
  if (config.subtitle) lines.push(`"${config.subtitle}"`)
  lines.push('')

  if (config.institution?.name) {
    lines.push(`"Institution:","${config.institution.name}"`)
    if (config.institution.address) lines.push(`"Address:","${config.institution.address}"`)
    if (config.institution.phone) lines.push(`"Phone:","${config.institution.phone}"`)
    if (config.institution.email) lines.push(`"Email:","${config.institution.email}"`)
    lines.push('')
  }

  if (config.dateRange) {
    lines.push(`"Report Period:","${config.dateRange.from} to ${config.dateRange.to}"`)
  }
  lines.push(`"Generated:","${config.generatedAt || new Date().toLocaleString()}"`)
  lines.push('')

  // Summary section
  if (config.summary && config.summary.length > 0) {
    lines.push('"SUMMARY"')
    config.summary.forEach(item => {
      lines.push(`"${item.label}","${item.value}"`)
    })
    lines.push('')
  }

  // Data sections
  config.sections.forEach(section => {
    lines.push(`"${section.title}"`)

    // Headers
    const headers = section.columns.map(col => `"${col.header}"`).join(',')
    lines.push(headers)

    // Data rows
    section.data.forEach(row => {
      const values = section.columns.map(col => {
        const value = row[col.key]
        if (value === null || value === undefined) return '""'
        if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`
        return `"${value}"`
      }).join(',')
      lines.push(values)
    })
    lines.push('')
  })

  // Create and download file
  const csvContent = lines.join('\n')
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `${config.filename}.csv`)
}

/**
 * Export data to XLSX (Excel) format
 */
export const exportToXLSX = (config: ExportConfig): void => {
  const workbook = XLSX.utils.book_new()

  // Create main data worksheet
  const wsData: any[][] = []

  // Header section
  wsData.push([config.title])
  if (config.subtitle) wsData.push([config.subtitle])
  wsData.push([])

  if (config.institution?.name) {
    wsData.push(['Institution:', config.institution.name])
    if (config.institution.address) wsData.push(['Address:', config.institution.address])
    if (config.institution.phone) wsData.push(['Phone:', config.institution.phone])
    if (config.institution.email) wsData.push(['Email:', config.institution.email])
    wsData.push([])
  }

  if (config.dateRange) {
    wsData.push(['Report Period:', `${config.dateRange.from} to ${config.dateRange.to}`])
  }
  wsData.push(['Generated:', config.generatedAt || new Date().toLocaleString()])
  wsData.push([])

  // Summary section
  if (config.summary && config.summary.length > 0) {
    wsData.push(['SUMMARY'])
    config.summary.forEach(item => {
      wsData.push([item.label, item.value])
    })
    wsData.push([])
  }

  // Data sections
  config.sections.forEach(section => {
    wsData.push([section.title])
    wsData.push(section.columns.map(col => col.header))

    section.data.forEach(row => {
      wsData.push(section.columns.map(col => row[col.key] ?? ''))
    })
    wsData.push([])
  })

  const worksheet = XLSX.utils.aoa_to_sheet(wsData)

  // Set column widths
  const maxCols = Math.max(...config.sections.map(s => s.columns.length), 2)
  worksheet['!cols'] = Array(maxCols).fill({ wch: 20 })

  // Style the title row
  if (worksheet['A1']) {
    worksheet['A1'].s = {
      font: { bold: true, sz: 16, color: { rgb: '2563EB' } },
      alignment: { horizontal: 'left' }
    }
  }

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report')

  // Create separate worksheets for each section with data
  config.sections.forEach((section, index) => {
    if (section.data.length > 0) {
      const sectionWs = XLSX.utils.json_to_sheet(section.data, {
        header: section.columns.map(col => col.key)
      })

      // Add headers
      const headerRow = section.columns.map(col => col.header)
      XLSX.utils.sheet_add_aoa(sectionWs, [headerRow], { origin: 'A1' })

      // Set column widths
      sectionWs['!cols'] = section.columns.map(col => ({ wch: col.width || 15 }))

      const sheetName = section.title.slice(0, 31).replace(/[/\\?*[\]]/g, '')
      XLSX.utils.book_append_sheet(workbook, sectionWs, sheetName)
    }
  })

  // Generate and download file
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  downloadBlob(blob, `${config.filename}.xlsx`)
}

/**
 * Export data to PDF format with professional styling
 */
export const exportToPDF = async (config: ExportConfig): Promise<void> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 15
  let yPos = margin

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      doc.addPage()
      yPos = margin
      addHeader()
    }
  }

  // Add header with logo placeholder and institution info
  const addHeader = () => {
    // Header background
    doc.setFillColor(...BRAND_COLORS.primaryRgb)
    doc.rect(0, 0, pageWidth, 35, 'F')

    // Logo placeholder (circle with initials)
    doc.setFillColor(255, 255, 255)
    doc.circle(margin + 10, 17.5, 10, 'F')

    if (config.institution?.name) {
      const initials = config.institution.name
        .split(' ')
        .map(word => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()

      doc.setTextColor(...BRAND_COLORS.primaryRgb)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(initials, margin + 10, 19, { align: 'center' })
    }

    // Institution name
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text(config.institution?.name || 'SmartTag School', margin + 25, 14)

    // Institution details
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    const details: string[] = []
    if (config.institution?.address) details.push(config.institution.address)
    if (config.institution?.phone) details.push(`Tel: ${config.institution.phone}`)
    if (config.institution?.email) details.push(config.institution.email)
    if (details.length > 0) {
      doc.text(details.join(' | '), margin + 25, 20)
    }

    // Report title on the right
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text(config.title, pageWidth - margin, 14, { align: 'right' })

    if (config.dateRange) {
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(`${config.dateRange.from} to ${config.dateRange.to}`, pageWidth - margin, 20, { align: 'right' })
    }

    doc.setFontSize(7)
    doc.text(`Generated: ${config.generatedAt || new Date().toLocaleString()}`, pageWidth - margin, 26, { align: 'right' })

    yPos = 45
  }

  // Add footer
  const addFooter = () => {
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)

      // Footer line
      doc.setDrawColor(...BRAND_COLORS.primaryRgb)
      doc.setLineWidth(0.5)
      doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15)

      // Footer text
      doc.setTextColor(...BRAND_COLORS.textRgb)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text('SmartTag School Management System', margin, pageHeight - 10)
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' })
    }
  }

  // Start document
  addHeader()

  // Report title
  doc.setTextColor(...BRAND_COLORS.textRgb)
  doc.setFontSize(18)
  doc.setFont('helvetica', 'bold')
  doc.text(config.title, pageWidth / 2, yPos, { align: 'center' })
  yPos += 8

  if (config.subtitle) {
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text(config.subtitle, pageWidth / 2, yPos, { align: 'center' })
    yPos += 10
  }

  // Summary section
  if (config.summary && config.summary.length > 0) {
    checkPageBreak(40)

    // Summary header
    doc.setFillColor(...BRAND_COLORS.lightGrayRgb)
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 8, 2, 2, 'F')
    doc.setTextColor(...BRAND_COLORS.primaryRgb)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Summary', margin + 4, yPos + 5.5)
    yPos += 12

    // Summary cards
    const cardWidth = (pageWidth - (margin * 2) - 10) / Math.min(config.summary.length, 4)
    const cardsPerRow = Math.min(config.summary.length, 4)

    config.summary.forEach((item, index) => {
      const row = Math.floor(index / cardsPerRow)
      const col = index % cardsPerRow
      const xPos = margin + (col * (cardWidth + 3))
      const cardYPos = yPos + (row * 22)

      // Card background
      doc.setFillColor(250, 250, 250)
      doc.setDrawColor(220, 220, 220)
      doc.roundedRect(xPos, cardYPos, cardWidth, 18, 2, 2, 'FD')

      // Card content
      doc.setTextColor(100, 100, 100)
      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.text(item.label, xPos + 4, cardYPos + 6)

      doc.setTextColor(...BRAND_COLORS.textRgb)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text(String(item.value), xPos + 4, cardYPos + 14)
    })

    const summaryRows = Math.ceil(config.summary.length / cardsPerRow)
    yPos += (summaryRows * 22) + 8
  }

  // Data sections
  for (const section of config.sections) {
    if (section.data.length === 0) continue

    checkPageBreak(30)

    // Section header
    doc.setFillColor(...BRAND_COLORS.primaryRgb)
    doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 8, 2, 2, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text(section.title, margin + 4, yPos + 5.5)
    yPos += 12

    // Table
    const tableHeaders = section.columns.map(col => col.header)
    const tableData = section.data.map(row =>
      section.columns.map(col => {
        const value = row[col.key]
        if (value === null || value === undefined) return ''
        return String(value)
      })
    )

    autoTable(doc, {
      startY: yPos,
      head: [tableHeaders],
      body: tableData,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: BRAND_COLORS.textRgb,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: BRAND_COLORS.secondaryRgb,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'left',
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      columnStyles: section.columns.reduce((acc, col, index) => {
        acc[index] = { cellWidth: col.width ? col.width * 0.35 : 'auto' }
        return acc
      }, {} as Record<number, any>),
      didDrawPage: () => {
        // Re-add header on new pages
        if (doc.getCurrentPageInfo().pageNumber > 1) {
          addHeader()
        }
      },
    })

    yPos = (doc as any).lastAutoTable.finalY + 10
  }

  // Add footer to all pages
  addFooter()

  // Save the PDF
  doc.save(`${config.filename}.pdf`)
}

/**
 * Helper function to download a blob
 */
const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

/**
 * Main export function that handles all formats
 */
export const exportReport = async (
  format: ExportFormat,
  config: ExportConfig
): Promise<void> => {
  switch (format) {
    case 'csv':
      exportToCSV(config)
      break
    case 'xlsx':
      exportToXLSX(config)
      break
    case 'pdf':
      await exportToPDF(config)
      break
    default:
      throw new Error(`Unsupported export format: ${format}`)
  }
}
